import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import SideMenu from '../components/SideMenu';
import { Menu, Plus, Search, Eye, Trash2, ArrowLeft, Camera, Calendar, User, Phone, MapPin, Mail } from 'lucide-react';
import { toast } from 'sonner';

const SelectionsList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selections, setSelections] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectionToDelete, setSelectionToDelete] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [selectionsRes, customersRes] = await Promise.all([
        axiosInstance.get('/selections'),
        axiosInstance.get('/customers')
      ]);
      
      setSelections(selectionsRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
      // Don't show error toast if it's just empty data
      if (err.response && err.response.status !== 404) {
        toast.error('Failed to load selections');
      }
      // Set empty arrays so UI doesn't break
      setSelections([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const getCustomer = (customerId) => {
    return customers.find(c => c.id === customerId);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/selections/${selectionToDelete}`);
      toast.success('Selection deleted successfully');
      setShowDeleteModal(false);
      setSelectionToDelete(null);
      loadData();
    } catch (err) {
      console.error('Error deleting selection:', err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          err.message || 
                          'Failed to delete selection';
      toast.error(errorMessage);
    }
  };

  const filteredSelections = selections.filter(selection => {
    const customer = getCustomer(selection.customer_id);
    const query = searchQuery.toLowerCase();
    
    return (
      selection.selection_id.toLowerCase().includes(query) ||
      (customer?.name || '').toLowerCase().includes(query) ||
      (customer?.mobile || '').includes(query) ||
      (customer?.alternate_mobile || '').includes(query) ||
      (customer?.address || '').toLowerCase().includes(query)
    );
  });

  const getStatusBadge = (status) => {
    const styles = {
      Active: 'bg-green-100 text-green-700 border-green-300',
      Converted: 'bg-blue-100 text-blue-700 border-blue-300',
      Cancelled: 'bg-red-100 text-red-700 border-red-300'
    };
    return styles[status] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="flex">
          <SideMenu user={user} onLogout={onLogout} isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
          <div className="flex-1 min-h-screen">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="flex">
        <SideMenu user={user} onLogout={onLogout} isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
        
        <div className="flex-1 min-h-screen">
          {/* Header */}
          <div className="bg-white border-b-2 border-amber-200 shadow-sm sticky top-0 z-10">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="p-2 hover:bg-amber-100 rounded-lg transition-colors lg:hidden touch-target"
                  >
                    <Menu className="w-5 h-5 text-gray-700" />
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="p-2 hover:bg-amber-100 rounded-lg transition-colors hidden lg:block touch-target"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-700" />
                  </button>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Customer Selections</h1>
                    <p className="text-xs sm:text-sm text-gray-500">Manage customer item preferences</p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/selections/create')}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold text-sm sm:text-base touch-target shadow-lg"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">New Selection</span>
                  <span className="sm:hidden">New</span>
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search by ID, customer name, mobile, address..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 sm:py-3 border-2 border-amber-200 rounded-lg focus:outline-none focus:border-amber-500 text-sm sm:text-base"
                />
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="bg-white border-b border-amber-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3">
              <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-amber-600" />
                  <span className="font-semibold text-gray-700">Total Selections:</span>
                  <span className="font-bold text-amber-700">{filteredSelections.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Selections List */}
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
            {filteredSelections.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🖼️</div>
                <p className="text-gray-500 text-lg mb-4">
                  {searchQuery ? 'No selections found matching your search' : 'No selections found'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate('/selections/create')}
                    className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-semibold"
                  >
                    Create First Selection
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {filteredSelections.map((selection) => {
                  const customer = getCustomer(selection.customer_id);
                  
                  return (
                    <div
                      key={selection.selection_id}
                      className="bg-white border-2 border-amber-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden"
                    >
                      <div className="p-4 sm:p-5">
                        {/* Top Row: ID, Date, Status, Actions */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between sm:justify-start gap-3 mb-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-base sm:text-lg text-amber-700 truncate">{selection.selection_id}</h3>
                                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mt-1">
                                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span>
                                    {new Date(selection.selection_date).toLocaleDateString('en-IN', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                              </div>
                              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold border-2 ${getStatusBadge(selection.status)} whitespace-nowrap flex-shrink-0`}>
                                {selection.status}
                              </span>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-2 flex-shrink-0">
                            <button
                              onClick={() => navigate(`/selections/${selection.selection_id}`)}
                              className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 font-semibold text-xs sm:text-sm touch-target"
                            >
                              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>View</span>
                            </button>
                            {selection.status === 'Active' && (
                              <button
                                onClick={() => {
                                  setSelectionToDelete(selection.selection_id);
                                  setShowDeleteModal(true);
                                }}
                                className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 touch-target flex-shrink-0"
                              >
                                <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Customer Details */}
                        <div className="space-y-2">
                          {customer && (
                            <>
                              {/* Name */}
                              <div className="flex items-start gap-2">
                                <User className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-gray-500">Customer Name</p>
                                  <p className="font-bold text-sm sm:text-base text-gray-800 break-words">{customer.name}</p>
                                </div>
                              </div>

                              {/* Primary Mobile */}
                              {customer.mobile && (
                                <div className="flex items-start gap-2">
                                  <Phone className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">Mobile</p>
                                    <p className="font-semibold text-sm text-gray-800">{customer.mobile}</p>
                                  </div>
                                </div>
                              )}

                              {/* Alternate Mobile */}
                              {customer.alternate_mobile && (
                                <div className="flex items-start gap-2">
                                  <Phone className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">Alternate Mobile</p>
                                    <p className="font-semibold text-sm text-gray-800">{customer.alternate_mobile}</p>
                                  </div>
                                </div>
                              )}

                              {/* Email */}
                              {customer.email && (
                                <div className="flex items-start gap-2">
                                  <Mail className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">Email</p>
                                    <p className="font-semibold text-xs sm:text-sm text-gray-800 break-all">{customer.email}</p>
                                  </div>
                                </div>
                              )}

                              {/* Address */}
                              {customer.address && (
                                <div className="flex items-start gap-2">
                                  <MapPin className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-500">Address</p>
                                    <p className="font-semibold text-xs sm:text-sm text-gray-800 break-words line-clamp-2">{customer.address}</p>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Bottom Section: Description & Rate/Advance Info */}
                        {(selection.items_description || selection.rate_policy === 'lock-today' || (selection.advance_payments && selection.advance_payments.length > 0)) && (
                          <div className="mt-4 pt-4 border-t border-amber-100 space-y-3">
                            {/* Items Description */}
                            {selection.items_description && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Items Description:</p>
                                <p className="text-xs sm:text-sm text-gray-700 break-words line-clamp-2">
                                  {selection.items_description}
                                </p>
                              </div>
                            )}

                            {/* Rate & Advance Badges */}
                            <div className="flex flex-wrap gap-2 text-xs">
                              {selection.rate_policy === 'lock-today' && selection.base24k_rate_ex_gst && (
                                <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full font-bold whitespace-nowrap border border-green-300">
                                  💰 ₹{selection.base24k_rate_ex_gst}/g Locked
                                </span>
                              )}
                              {selection.advance_payments && selection.advance_payments.length > 0 && (
                                <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full font-bold whitespace-nowrap border border-blue-300">
                                  💳 ₹{selection.advance_payments.reduce((sum, p) => {
                                    const isRefund = p.transaction_type === 'REFUND';
                                    return sum + (isRefund ? -parseFloat(p.amount || 0) : parseFloat(p.amount || 0));
                                  }, 0).toLocaleString('en-IN')} Advance
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Selection?</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this selection? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectionToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectionsList;
